/* @flow weak */
import * as React from 'react';
import invariant from 'invariant';

import DialogContent from 'components/DialogContent.jsx';
import enumerate from 'lib/enumerate';
import Link from 'components/Link.jsx';
import LinkSmsPhoneInputBox from 'user/components/LinkSmsPhoneInputBox.jsx';
import LinkSmsVerificationCodeBox from 'user/components/LinkSmsVerificationCodeBox.jsx';
import ToastActions from 'actions/ToastActions';
import Subscription from 'entities/Subscription';

const STATUS = enumerate(['ASK_PHONE', 'ASK_CODE']);

type Props = {
  /* return linked device */
  onDone: Function,
  /* optional - skip asking for the phone, ask for code directly */
  token?: ?string,
  /* optional - enable skipping */
  onSkip?: ?Function,
  /* optional - show modal close button */
  closable?: ?boolean,
  /* optional - group code this is required for instantResend  */
  groupCode?: ?string,
  /* optional - address eg (sms://+14156152427) is required for instantResend  */
  address?: ?string,
  /* optional - when true will resent code to mobile directly */
  instantResend?: ?boolean,
  onRequestHide?: ?Function,
  /* Skip updating device preferences:
   *
   * When adding a new device the backend
   * try to make smart choice about enabling the added device
   * and disabeling preixisting one. This skips it, the added
   * device will be disabled, and current devices will not change.
   */
  skipUpdateDevicePreferences?: boolean,
  userMakingACall?: ?boolean,
};

type State = {
  status: STATUS.ASK_CODE | STATUS.ASK_PHONE,
  // XXX shouldn't have props duplicated in state
  token: ?string,
  phoneNumber?: ?string,
};

const getStateFromProps = (props: Props): State => ({
  status: props.token ? STATUS.ASK_CODE : STATUS.ASK_PHONE,
  token: props.token || null,
});

class LinkSmsDevicePage extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = getStateFromProps(props);
  }

  handleDeviceLinked = (...args) => {
    this.props.onDone(...args);
  };

  handlePhoneNumberSubmit = result => {
    this.setState({
      status: STATUS.ASK_CODE,
      token: result.token,
      phoneNumber: result.phoneNumber,
    });
  };

  handleResend = () => {
    if (this.props.instantResend) {
      this.resendSms();
    } else {
      this.setState(getStateFromProps(this.props));
    }
  };

  resendSms = () => {
    invariant(this.props.groupCode, 'LinkSmsDevicePage group code required in resendSms');
    const onSuccess = response => {
      ToastActions.showSuccessToast('Code resent');
      this.setState({ token: response.token });
    };

    const onError = () => {
      ToastActions.showDefaultErrorToast();
    };

    Subscription.initiateDeviceSubscription(this.props.groupCode, this.props.address).then(
      onSuccess,
      onError
    );
  };

  renderAskConfirmationCode = () => {
    const { token, phoneNumber } = this.state;
    invariant(token, 'LinkSmsDevicePage must have a token in renderAskConfirmationCode');
    let footer = null;
    if (this.props.onSkip) {
      footer = (
        <span>
          or <Link onClick={this.props.onSkip}>skip this step</Link>
        </span>
      );
    }

    return (
      <LinkSmsVerificationCodeBox
        skipUpdateDevicePreferences={this.props.skipUpdateDevicePreferences}
        closable={this.props.closable}
        onDone={this.handleDeviceLinked}
        token={token}
        phoneNumber={phoneNumber}
        onResend={this.handleResend}
        footer={footer}
      />
    );
  };

  renderAskPhoneNumber = () => {
    let footer = null;
    if (this.props.onSkip) {
      footer = (
        <span>
          Don’t want to connect a phone?{' '}
          <Link onClick={this.props.onSkip} data-test-selector="link-sms-skip">
            Skip this step
          </Link>
        </span>
      );
    }
    return (
      <LinkSmsPhoneInputBox
        onRequestHide={this.props.onRequestHide}
        closable={this.props.closable}
        footer={footer}
        onDone={this.handlePhoneNumberSubmit}
        userMakingACall={this.props.userMakingACall}
      />
    );
  };

  renderBody = () => {
    return {
      [STATUS.ASK_PHONE]: this.renderAskPhoneNumber,
      [STATUS.ASK_CODE]: this.renderAskConfirmationCode,
    }[this.state.status]();
  };

  render() {
    return <DialogContent className="LinkSmsDevicePage">{this.renderBody()}</DialogContent>;
  }
}

export default LinkSmsDevicePage;
